import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import client from './client'

export function useAdminLookup(type) {
  return useQuery({
    queryKey: ['admin-lookup', type],
    queryFn: () => client.get(`/api/admin/${type}`).then((r) => r.data),
  })
}

export function useCreateLookup(type) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => client.post(`/api/admin/${type}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-lookup', type] })
      qc.invalidateQueries({ queryKey: ['meta'] })
    },
  })
}

export function useUpdateLookup(type) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ code, name }) => client.put(`/api/admin/${type}/${code}`, { name }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-lookup', type] })
      qc.invalidateQueries({ queryKey: ['meta'] })
    },
  })
}

export function useDeleteLookup(type) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code) => client.delete(`/api/admin/${type}/${code}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-lookup', type] })
      qc.invalidateQueries({ queryKey: ['meta'] })
    },
  })
}

export function useAdminManga(id) {
  return useQuery({
    queryKey: ['admin-manga', id],
    queryFn: () => client.get(`/api/admin/manga/${id}`).then((r) => r.data),
    enabled: !!id,
    retry: false,
  })
}

export function useAdminMangaList(page = 1, filters = {}) {
  return useQuery({
    queryKey: ['admin-manga', page, filters],
    queryFn: () => client.get('/api/admin/manga', { params: { page, limit: 20, ...filters } }).then((r) => r.data),
  })
}

export function useCreateManga() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => client.post('/api/admin/manga', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-manga'] }),
  })
}

export function useUpdateManga(mangaId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => client.put(`/api/admin/manga/${mangaId}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-manga'] })
      qc.invalidateQueries({ queryKey: ['manga', mangaId] })
    },
  })
}

export function useUploadCover() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mangaId, file }) => {
      const fd = new FormData()
      fd.append('file', file)
      return client.post(`/api/admin/manga/${mangaId}/cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data)
    },
    onSuccess: (data, { mangaId }) => {
      qc.invalidateQueries({ queryKey: ['admin-manga'] })
      qc.invalidateQueries({ queryKey: ['manga', mangaId] })
    },
  })
}

export function useDeleteManga() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mangaId) => client.delete(`/api/admin/manga/${mangaId}`),
    onSuccess: (_, mangaId) => {
      qc.removeQueries({ queryKey: ['admin-manga', mangaId] })
      qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'admin-manga' && q.queryKey[1] !== mangaId,
      })
    },
  })
}

export function usePublishManga() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mangaId) => client.patch(`/api/admin/manga/${mangaId}/publish`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-manga'] }),
  })
}

export function useAdminVolumes(mangaId) {
  return useQuery({
    queryKey: ['admin-volumes', mangaId],
    queryFn: () =>
      client.get(`/api/admin/manga/${mangaId}/volumes`).then((r) => r.data?.items ?? r.data),
    enabled: !!mangaId,
  })
}

export function useAdminChapters(mangaId) {
  return useQuery({
    queryKey: ['admin-all-chapters', mangaId],
    queryFn: () =>
      client.get(`/api/admin/manga/${mangaId}/chapters`).then((r) => r.data?.items ?? r.data),
    enabled: !!mangaId,
  })
}

export function useCreateVolume(mangaId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => client.post(`/api/admin/manga/${mangaId}/volumes`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-volumes', mangaId] }),
  })
}

export function useCreateChapter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ volumeId, ...data }) =>
      client.post(`/api/admin/volumes/${volumeId}/chapters`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-all-chapters'] })
      qc.invalidateQueries({ queryKey: ['admin-chapters'] })
    },
  })
}

export function useDeleteChapter(mangaId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ volumeId, chapterId }) =>
      client.delete(`/api/admin/volumes/${volumeId}/chapters/${chapterId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-all-chapters', mangaId] })
      qc.invalidateQueries({ queryKey: ['manga', mangaId] })
    },
  })
}

export function useUploadChapter() {
  return useMutation({
    mutationFn: ({ chapterId, formData, onProgress }) =>
      client.post(`/api/admin/chapters/${chapterId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total))
        },
      }).then((r) => r.data),
  })
}

export function useAdminUsers(page = 1, q = '') {
  return useQuery({
    queryKey: ['admin-users', page, q],
    queryFn: () => client.get('/api/admin/users', { params: { page, limit: 20, ...(q ? { q } : {}) } }).then((r) => r.data),
  })
}

export function useUpdateUser(userId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => client.patch(`/api/admin/users/${userId}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId) => client.delete(`/api/admin/users/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useFreezeUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, reason, until }) => client.post(`/api/admin/users/${userId}/freeze`, { reason, until }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useUnfreezeUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId) => client.post(`/api/admin/users/${userId}/unfreeze`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useBanUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, reason }) => client.post(`/api/admin/users/${userId}/ban`, { reason }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useUnbanUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId) => client.post(`/api/admin/users/${userId}/unban`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useWarnUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, reason }) => client.post(`/api/admin/users/${userId}/warn`, { reason }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useAdminReports(page = 1, status = '') {
  return useQuery({
    queryKey: ['admin-reports', page, status],
    queryFn: () => client.get('/api/admin/reports', { params: { page, limit: 20, ...(status ? { status } : {}) } }).then((r) => r.data),
    refetchInterval: 10000,
  })
}

export function useUpdateReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ reportId, status }) => client.patch(`/api/admin/reports/${reportId}`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reports'] }),
  })
}

export function useAdminSupportTickets(page = 1, status = '') {
  return useQuery({
    queryKey: ['admin-support', page, status],
    queryFn: () => client.get('/api/admin/support', { params: { page, limit: 20, ...(status ? { status } : {}) } }).then((r) => r.data),
    refetchInterval: 10000,
  })
}

export function useAdminSupportTicket(ticketId) {
  return useQuery({
    queryKey: ['admin-support-ticket', ticketId],
    queryFn: () => client.get(`/api/admin/support/${ticketId}`).then((r) => r.data),
    enabled: !!ticketId,
    refetchInterval: 5000,
  })
}

export function useAdminReplyTicket(ticketId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (text) => client.post(`/api/admin/support/${ticketId}/reply`, { text }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-support-ticket', ticketId] })
      qc.invalidateQueries({ queryKey: ['admin-support'] })
    },
  })
}

export function useAdminCloseTicket(ticketId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => client.post(`/api/admin/support/${ticketId}/close`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-support-ticket', ticketId] })
      qc.invalidateQueries({ queryKey: ['admin-support'] })
    },
  })
}
